import { createImageTexture, gl } from "./gl.js";
import {
  appState,
  titleSprite,
  titleHelpSprite,
  luckyBagSprite,
  luckyBagBlueSprite,
  giftIconSprite,
  blueGiftIconSprite,
  missionButtonSprite,
  languageState,
  rewardBackgroundSprite,
  rewardWinSprite,
  rewardLoseSprite,
  oneMoreSprites,
  rosterState,
  selectionArrowSprite,
  selectionAuraSprite,
  selectionRingSprite,
  selectionPromptSprite,
  textures,
  turnBannerSprites,
  yutButtonIconSprite,
  yutButtonGlowSprite,
  yutButtonTexture,
  yutButtonTitleSprite,
  yutResultSprites,
  yutSprites,
} from "./state.js";

let atlasFrames = null;
let atlasPages = null;
let atlasReady = false;
let atlasFailed = false;
function toWebpPath(src) {
  return src.replace(/\.png$/i, ".webp");
}

export function loadAtlas() {
  atlasReady = false;
  atlasFailed = false;
  loadAtlasAsync().catch(() => {
    atlasFailed = true;
  });
}

export async function setLanguageSprites(lang) {
  await applyLanguageSprites(lang);
  appState.requestDraw?.();
}

async function loadAtlasAsync() {
  const atlasFiles = await loadAtlasManifest();
  let entries = await loadAtlasEntries(atlasFiles);
  if (!entries.length && atlasFiles[0] !== "game-atlas-0.json") {
    entries = await loadAtlasEntries(["game-atlas-0.json"]);
  }
  if (!entries.length) {
    atlasFailed = true;
    return;
  }

  atlasPages = entries.map((entry) => ({
    texture: createImageTexture(gl, entry.image),
    width: entry.image.width,
    height: entry.image.height,
    image: entry.image,
    src: entry.src || null,
  }));

  atlasFrames = {};
  entries.forEach((entry, index) => {
    const frames = entry.data.frames || {};
    for (const key of Object.keys(frames)) {
      atlasFrames[key] = { frameData: frames[key], pageIndex: index };
    }
  });
  atlasReady = true;
  atlasFailed = false;

  const firstPage = atlasPages[0];
  if (firstPage) {
    textures.atlas.texture = firstPage.texture;
    textures.atlas.width = firstPage.width;
    textures.atlas.height = firstPage.height;
  }

  applySprite(textures.background, "back.png");
  textures.backgroundSize = {
    width: textures.background.sourceWidth || textures.background.width,
    height: textures.background.sourceHeight || textures.background.height,
  };

  applySprite(textures.board, "bg_yutnori.png");
  textures.boardSize = {
    width: textures.board.sourceWidth || textures.board.width,
    height: textures.board.sourceHeight || textures.board.height,
  };

  applyFrameSet(yutSprites.throwFrames, "A_ThrowYut");
  for (const entry of yutSprites.results) {
    applyFrameSet(entry.frames, entry.frameKey);
  }

  applySprite(yutButtonTexture, "btn_yutnori.png");
  applySprite(yutButtonGlowSprite, "btn_yutnori_glow.png");
  applySprite(yutButtonIconSprite, "ic_yutnori_00.png");
  applySprite(titleHelpSprite, "btn_help_event_yutnori_00.png");
  applySprite(luckyBagSprite, "lucky_bag.png");
  applySprite(luckyBagBlueSprite, "lucky_bag_blue.png");
  applySprite(giftIconSprite, "ic_gift_01.png");
  await applySpriteWithFallback(
    blueGiftIconSprite,
    ["ic_exchange_02.png"],
    "assets/image/ic_exchange_02.png"
  );
  applySprite(missionButtonSprite, "btn_event_mission_web.png");
  applySprite(selectionArrowSprite, "web_arrow_notice_minigame_00.png");
  createSelectionAuraSprite(selectionAuraSprite);
  createSelectionRingSprite(selectionRingSprite);
  applySprite(selectionPromptSprite, "gradation_yutnori.png");
  await applyLanguageSprites(languageState.current);
  if (!applyPixelatedSprite(rewardWinSprite, "yutnori_txt_win.png")) {
    applySprite(rewardWinSprite, "yutnori_txt_win.png");
  }
  if (!applyPixelatedSprite(rewardLoseSprite, "yutnori_txt_lose.png")) {
    applySprite(rewardLoseSprite, "yutnori_txt_lose.png");
  }
  applySprite(rewardBackgroundSprite, "gradation_yutnori.png");

  for (const token of rosterState.tokens) {
    if (token.idlePath) {
      token.ready = applySprite(token.idle, toAtlasKey(token.idlePath));
    }
    if (token.jumpPath) {
      applySprite(token.jump, toAtlasKey(token.jumpPath));
    }
  }

  if (typeof appState.onAtlasReady === "function") {
    appState.onAtlasReady();
  }
  appState.requestDraw?.();
}

function createSelectionAuraSprite(sprite, size = 256) {
  if (!sprite) {
    return;
  }
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }
  ctx.clearRect(0, 0, size, size);
  const cx = size * 0.5;
  const cy = size * 0.5;
  const radius = size * 0.5;
  const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
  gradient.addColorStop(0, "rgba(255, 255, 255, 0.7)");
  gradient.addColorStop(0.55, "rgba(255, 255, 255, 0.35)");
  gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  sprite.texture = createImageTexture(gl, canvas);
  sprite.width = size;
  sprite.height = size;
  sprite.sourceWidth = size;
  sprite.sourceHeight = size;
  sprite.trimX = 0;
  sprite.trimY = 0;
  sprite.trimWidth = size;
  sprite.trimHeight = size;
  sprite.trimmed = false;
  sprite.uv = null;
}

function createSelectionRingSprite(sprite, size = 256) {
  if (!sprite) {
    return;
  }
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }
  ctx.clearRect(0, 0, size, size);
  const cx = size * 0.5;
  const cy = size * 0.5;
  const radius = size * 0.45;
  const thickness = size * 0.08;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
  ctx.lineWidth = thickness;
  ctx.shadowColor = "rgba(255, 255, 255, 0.6)";
  ctx.shadowBlur = size * 0.08;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();

  sprite.texture = createImageTexture(gl, canvas);
  sprite.width = size;
  sprite.height = size;
  sprite.sourceWidth = size;
  sprite.sourceHeight = size;
  sprite.trimX = 0;
  sprite.trimY = 0;
  sprite.trimWidth = size;
  sprite.trimHeight = size;
  sprite.trimmed = false;
  sprite.uv = null;
}

async function applyLanguageSprites(lang) {
  const normalized = typeof lang === "string" && lang.toLowerCase().startsWith("en") ? "en" : "ko";
  const suffix = normalized === "en" ? "_en" : "_ko";
  const titleKeys = [`title_yutnori_00${suffix}.png`, `title_yunnori_00${suffix}.png`];
  const resultKeys = {
    do: [`ic_yutnori_result_do${suffix}.png`, `ic_yurnori_result_do${suffix}.png`],
    gae: [`ic_yutnori_result_gae${suffix}.png`, `ic_yurnori_result_gae${suffix}.png`],
    geol: [`ic_yutnori_result_geol${suffix}.png`, `ic_yurnori_result_geol${suffix}.png`],
    yut: [`ic_yutnori_result_yut${suffix}.png`, `ic_yurnori_result_yut${suffix}.png`],
    mo: [`ic_yutnori_result_mo${suffix}.png`, `ic_yurnori_result_mo${suffix}.png`],
  };
  const turnBannerKeys = {
    player: [`ic_yutnori_ourturn${suffix}.png`],
    monster: [`ic_yutnori_enemyturn${suffix}.png`],
  };

  await applySpriteWithFallback(
    titleSprite,
    titleKeys,
    `assets/image/${titleKeys[0]}`
  );
  await applySpriteWithFallback(
    yutButtonTitleSprite,
    [`btn_txt_yutnori_00${suffix}.png`],
    `assets/image/btn_txt_yutnori_00${suffix}.png`
  );
  await applySpriteWithFallback(
    yutResultSprites.do,
    resultKeys.do,
    `assets/image/${resultKeys.do[0]}`
  );
  await applySpriteWithFallback(
    yutResultSprites.gae,
    resultKeys.gae,
    `assets/image/${resultKeys.gae[0]}`
  );
  await applySpriteWithFallback(
    yutResultSprites.geol,
    resultKeys.geol,
    `assets/image/${resultKeys.geol[0]}`
  );
  await applySpriteWithFallback(
    yutResultSprites.yut,
    resultKeys.yut,
    `assets/image/${resultKeys.yut[0]}`
  );
  await applySpriteWithFallback(
    yutResultSprites.mo,
    resultKeys.mo,
    `assets/image/${resultKeys.mo[0]}`
  );
  await applySpriteWithFallback(
    turnBannerSprites.player,
    turnBannerKeys.player,
    `assets/image/${turnBannerKeys.player[0]}`
  );
  await applySpriteWithFallback(
    turnBannerSprites.monster,
    turnBannerKeys.monster,
    `assets/image/${turnBannerKeys.monster[0]}`
  );

  await applySpriteWithFallback(
    oneMoreSprites.one,
    [`one_more_time${suffix}.png`],
    `assets/image/one_more_time${suffix}.png`
  );
  await applySpriteWithFallback(
    oneMoreSprites.two,
    [`two_more_time${suffix}.png`],
    `assets/image/two_more_time${suffix}.png`
  );
  await applySpriteWithFallback(
    oneMoreSprites.three,
    [`three_more_time${suffix}.png`],
    `assets/image/three_more_time${suffix}.png`
  );
  await applySpriteWithFallback(
    oneMoreSprites.four,
    [`four_more_time${suffix}.png`],
    `assets/image/four_more_time${suffix}.png`
  );
}

async function applySpriteWithFallback(sprite, keys, fallbackUrl) {
  if (!Array.isArray(keys)) {
    return;
  }
  for (const key of keys) {
    if (key && applySprite(sprite, key)) {
      return;
    }
  }
  if (!atlasReady && !atlasFailed) {
    return;
  }
  if (fallbackUrl) {
    await applyStandaloneSprite(sprite, fallbackUrl);
  }
}

function toAtlasKey(assetPath) {
  return assetPath.replace(/^assets\/image\//, "");
}

function getFrameEntry(key) {
  if (!atlasFrames) {
    return null;
  }
  return atlasFrames[key] || null;
}

function applySprite(sprite, key) {
  const entry = getFrameEntry(key);
  if (!entry || !atlasPages) {
    return false;
  }
  const frameData = entry.frameData;
  if (!frameData || !frameData.frame) {
    return false;
  }
  const page = atlasPages[entry.pageIndex];
  if (!page) {
    return false;
  }
  const frame = frameData.frame;
  sprite.texture = page.texture;
  sprite.width = frame.w;
  sprite.height = frame.h;
  const source = frameData.sourceSize || { w: frame.w, h: frame.h };
  const sourceRect = frameData.spriteSourceSize || { x: 0, y: 0, w: frame.w, h: frame.h };
  sprite.sourceWidth = source.w;
  sprite.sourceHeight = source.h;
  sprite.trimX = sourceRect.x;
  sprite.trimY = sourceRect.y;
  sprite.trimWidth = sourceRect.w;
  sprite.trimHeight = sourceRect.h;
  sprite.trimmed = !!frameData.trimmed;
  sprite.uv = getFrameUv(frame, page.width, page.height);
  return true;
}

function applyPixelatedSprite(sprite, key) {
  const entry = getFrameEntry(key);
  if (!entry || !atlasPages) {
    return false;
  }
  const frameData = entry.frameData;
  const page = atlasPages[entry.pageIndex];
  if (!frameData || !frameData.frame || !page?.image) {
    return false;
  }
  const frame = frameData.frame;
  const source = frameData.sourceSize || { w: frame.w, h: frame.h };
  const sourceRect = frameData.spriteSourceSize || { x: 0, y: 0, w: frame.w, h: frame.h };
  const canvas = document.createElement("canvas");
  canvas.width = source.w;
  canvas.height = source.h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return false;
  }
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(
    page.image,
    frame.x,
    frame.y,
    frame.w,
    frame.h,
    sourceRect.x,
    sourceRect.y,
    sourceRect.w,
    sourceRect.h
  );

  const texture = createImageTexture(gl, canvas);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

  sprite.texture = texture;
  sprite.width = source.w;
  sprite.height = source.h;
  sprite.sourceWidth = source.w;
  sprite.sourceHeight = source.h;
  sprite.trimX = 0;
  sprite.trimY = 0;
  sprite.trimWidth = source.w;
  sprite.trimHeight = source.h;
  sprite.trimmed = false;
  sprite.uv = null;
  return true;
}

function applyFrameSet(frames, prefix) {
  for (let i = 0; i < frames.length; i += 1) {
    const key = `${prefix}_${String(i).padStart(2, "0")}.png`;
    applySprite(frames[i], key);
  }
}

function getFrameUv(frame, atlasWidth, atlasHeight) {
  const u0 = frame.x / atlasWidth;
  const u1 = (frame.x + frame.w) / atlasWidth;
  const vTop = frame.y / atlasHeight;
  const vBottom = (frame.y + frame.h) / atlasHeight;
  const v0 = 1 - vBottom;
  const v1 = 1 - vTop;
  return { u0, v0, u1, v1 };
}

async function applyStandaloneSprite(sprite, src) {
  try {
    const image = await loadImageSmart(src);
    sprite.texture = createImageTexture(gl, image);
    sprite.width = image.width;
    sprite.height = image.height;
    sprite.sourceWidth = image.width;
    sprite.sourceHeight = image.height;
    sprite.uv = null;
    sprite.trimX = 0;
    sprite.trimY = 0;
    sprite.trimWidth = image.width;
    sprite.trimHeight = image.height;
    sprite.trimmed = false;
  } catch (err) {
    // Ignore load errors for standalone sprites.
  }
}

async function loadAtlasEntries(files) {
  const entries = [];
  for (const file of files) {
    const data = await fetch(`assets/atlas/${file}`).then((res) => (res.ok ? res.json() : null));
    if (!data || !data.meta || !data.meta.image) {
      continue;
    }
    const { image, src } = await loadImageSmartWithSrc(`assets/atlas/${data.meta.image}`);
    entries.push({ data, image, src });
  }
  return entries;
}

async function loadAtlasManifest() {
  try {
    const response = await fetch("assets/atlas/atlas-manifest.json");
    if (response.ok) {
      const manifest = await response.json();
      if (manifest && Array.isArray(manifest.atlases) && manifest.atlases.length) {
        return manifest.atlases;
      }
    }
  } catch (err) {
    // Ignore manifest errors and fall back.
  }
  return ["game-atlas.json"];
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

async function loadImageSmartWithSrc(src) {
  const isPng = typeof src === "string" && src.toLowerCase().endsWith(".png");
  if (isPng) {
    const webpPath = toWebpPath(src);
    try {
      const image = await loadImage(webpPath);
      return { image, src: webpPath };
    } catch (err) {
      // Fall back to PNG.
    }
  }
  const image = await loadImage(src);
  return { image, src };
}

async function loadImageSmart(src) {
  const isPng = typeof src === "string" && src.toLowerCase().endsWith(".png");
  if (isPng) {
    try {
      return await loadImage(toWebpPath(src));
    } catch (err) {
      // Fall back to PNG.
    }
  }
  return loadImage(src);
}

export function getAtlasFrameInfo(key) {
  if (!atlasFrames || !atlasPages || !key) {
    return null;
  }
  const entry = atlasFrames[key];
  if (!entry || !entry.frameData) {
    return null;
  }
  const page = atlasPages[entry.pageIndex];
  if (!page) {
    return null;
  }
  return {
    frame: entry.frameData.frame || null,
    sourceSize: entry.frameData.sourceSize || null,
    trimmed: !!entry.frameData.trimmed,
    spriteSourceSize: entry.frameData.spriteSourceSize || null,
    pageIndex: entry.pageIndex,
    pageWidth: page.width,
    pageHeight: page.height,
    pageSrc: page.src,
  };
}
